import { ActionIcon, Group, Space, Stack, Title } from '@mantine/core';
import { ProfessionalSearchInput } from '../ProfessionalSearchInput';
import { motion } from 'motion/react';
import { UserCard, UserCardSkeletonLoader } from '../userCard/UserCard';
import { useApp } from '../../../../context/AppContext';
import { IconFilter } from '@tabler/icons-react';
import { NoSearchResults } from './NoResults';
import { useSearch } from './SearchContext';
import { primaryColor } from '../../../../constants/theme';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export const SearchResultsStack = () => {
  const { profile } = useApp();
  const { isTabletAndBelow } = useApp();
  const [searchParams] = useSearchParams();
  const {
    searchExpression,
    searchResults,
    setFiltersOpen,
    onClickOption,
    setCategoryFilters,
  } = useSearch();

  const categoryParam = searchParams.get('categories');
  const categoryDisplay = categoryParam
    ? categoryParam
      .split(',')
      .map((category) => category.trim())
      .filter(Boolean)
      .join(', ')
    : undefined;
  const searchInputValue =
    searchExpression.searchText === '*' && categoryDisplay
      ? categoryDisplay
      : searchExpression.searchText;

  const handleSearch = (search: string) => {
    if (search === '*' && categoryParam) {
      setCategoryFilters(new Set());
    }

    onClickOption(search);
  };

  return (
    <Stack p={'md'}>
      <ProfessionalSearchInput
        onClickOption={handleSearch}
        redirectOnClear={true}
        initialSearchValue={searchInputValue}
      />
      <Group justify="space-between">
        <Title order={4} fw="normal">
          Search Results
        </Title>
        {isTabletAndBelow && (
          <ActionIcon
            disabled={
              !searchResults?.facets ||
              Object.values(searchResults.facets).every(
                (facetArray) => facetArray.length === 0
              )
            }
            variant="subtle"
            size="lg"
            color={searchExpression?.filterExpression ? primaryColor : 'grey'}
            onClick={() => setFiltersOpen(true)}
          >
            <IconFilter></IconFilter>
          </ActionIcon>
        )}
      </Group>
      <SearchResultsInfiniteLoader filterProfileId={profile?.id ?? ''} />
    </Stack>
  );
};

export const SimilarProfessionalsStack = ({
  categories,
  currentProfessionalId,
}: {
  categories: string[];
  currentProfessionalId: string;
}) => {
  const { onSetFilterExpression } = useSearch();

  useEffect(() => {
    if (categories.length > 0) {
      onSetFilterExpression(
        `(${categories.map((category) => `search.ismatch('${category}', 'categories', 'simple', 'all')`).join(' or ')})`
      );
    } else {
      onSetFilterExpression('');
    }
  }, [categories]);

  return (
    <Stack p={'md'}>
      <Title order={3} fw="normal">
        Similar Professionals
      </Title>
      <SearchResultsInfiniteLoader filterProfileId={currentProfessionalId} />
    </Stack>
  );
};

export const SearchResultsInfiniteLoader = ({
  filterProfileId,
}: {
  filterProfileId: string;
}) => {
  const {
    isLoading,
    allUsers = [],
    hasNextPage,
    isFetchingNextPage,
    sentryRef,
  } = useSearch();

  return (
    <>
      {!isLoading &&
        allUsers.map((result, index) => (
          <UserCard key={`${result.id}-${index}`} userDocument={result} />
        ))}
      {isLoading &&
        Array.from({ length: 6 }).map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <UserCardSkeletonLoader key={index} />
          </motion.div>
        ))}

      {!isLoading && allUsers.length === 0 && <NoSearchResults />}

      {/* Infinite scroll trigger */}
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={sentryRef}>
          {isFetchingNextPage &&
            Array.from({ length: 3 }).map((_, index) => (
              <motion.div
                key={`loading-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Space h={20} />
                <UserCardSkeletonLoader />
              </motion.div>
            ))}
        </div>
      )}
    </>
  );
};
